import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/template';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { templateTableTitles } from 'constants.js';
import { resetListsData } from 'store/lists/actions';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';

const {
  getPaginatedTemplates,
  deleteTemplates,
  unsetCurrentTemplate,
  setCurrentTemplate,
  warnAndArchiveTemplate,
  setCopyCurrentTemplate,
  getTemplateTags,
  updateCommunicationFilters,
  resetCommunicationFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const Template = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const action = getPaginatedTemplates({ force: true });
  const templates = useSelector(store => store.template.paginatedList);
  const filters = useSelector(
    store => store.template.filters
  );
  const isLoading = useSelector(store => store.template.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.productCategories
  );
  const tags = useSelector(store => store.template.tagList);
  const optionsToFilterType = [
    { name: 'type', value: 'EMAIL', checked: false },
    { name: 'type', value: 'SMS', checked: false }
  ];
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'GLOBAL DEFAULT',
      value: 'global',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];
  const optionsToFilterTags = tags
    ? tags.map(tag => {
        return { name: 'tags', value: tag, checked: false };
      })
    : [];
  const optionsToFilterMarketingType = [
    { name: 'marketingType', value: 'Promotional', checked: false },
    { name: 'marketingType', value: 'Transactional', checked: false }
  ];
  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedTemplates({ force: true }));
    dispatch(getTemplateTags());
    dispatch(resetListsData());
  }, []);

  const onClickArchive = uuid => {
    let action = deleteTemplates({
      templateUuids: [uuid],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Template', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog
    };
    if (filters?.displayActive) {
      dispatch(warnAndArchiveTemplate({ uuid: uuid, message: warnMessage }));
    } else {
      dispatch(action);
    }
  };


  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Templates`,
      action: () => onClickToggleArchive(action, updateCommunicationFilters, { displayActive: !filters?.displayActive, pageNo: 0 })
    },
    {
      name: 'Add Template',
      action: () => onClickAdd(unsetCurrentTemplate(), `/templates/details`)
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const getActionButtons = template => [
    {
      name: isTemplateReadOnly(template, isLoading)
        ? 'View Template'
        : 'Edit Template',
      action: ['ACTION', () => onEdit(template)],
      ...(isTemplateReadOnly(template, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(template)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    },
    {
      name: 'Copy Template',
      action: ['ACTION', () => onCopy(template.uuid)]
    },
    {
      name: filters?.displayActive ? 'Archive Template' : 'Activate Template',
      action: ['ACTION', () => onClickArchive(template.uuid)],
      ...(isTemplateReadOnly(template, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(template)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterType.map(option => {
          if (filters?.filterTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Type',
        values: filters?.filterTypes,
        onChange: types => dispatch(updateCommunicationFilters({ filterTypes: types, pageNo: 0 }))
      },
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateCommunicationFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateCommunicationFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateCommunicationFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      },
      {
        options: optionsToFilterMarketingType.map(option => {
          if (filters?.filterMarketingTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Marketing Type',
        values: filters?.filterMarketingTypes,
        onChange: marketingTypes =>
          dispatch(updateCommunicationFilters({ filterMarketingTypes: marketingTypes, pageNo: 0 }))
      }
      ],
    autocompleteSearch: {
      options: optionsToFilterTags.map(option => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Tags',
      values: filters?.filterTags,
      onChange: tags => dispatch(updateCommunicationFilters({ filterTags: tags, pageNo: 0 }))
    },
    resetFilters: () => dispatch(resetCommunicationFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Communication Templates',
    searchAction: (payload) => dispatch(updateCommunicationFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentTemplate(), setCurrentTemplate(row)],
      `/templates/details/${row.uuid}`
    );
  const onCopy = uuid =>
    onClickCopy(setCopyCurrentTemplate, `/templates/details`, uuid, templates);

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={templateTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={templates}
          action={getPaginatedTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateCommunicationFilters(payload))}
        />
      </div>
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  );
};

export default Template;
